import { istDate, istToday } from "@/lib/istDate";
import { useLive } from "@/lib/live";
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, MagnifyingGlass, Phone, Buildings, MapPin, Pencil, Trash,
  Package, CurrencyInr, Trophy, GridFour, Rows, FilePdf
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WeldersPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // welder or "new"
  const [busy, setBusy] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("welders.view") || "cards");
  const [exportOpen, setExportOpen] = useState(false);

  useLive(() => { load(); });
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/welders${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (w) => {
    if (!window.confirm(`Delete ${w.name}? Their past orders will still be visible but no longer linked.`)) return;
    try {
      await api.delete(`/welders/${w.id}`);
      toast.success("Welder removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setView = (v) => {
    setViewMode(v);
    localStorage.setItem("welders.view", v);
  };

  const totalRevenue = rows.reduce((s, w) => s + (w.total_revenue || 0), 0);
  const totalBalance = rows.reduce((s, w) => s + (w.total_balance || 0), 0);

  return (
    <div className="space-y-5" data-testid="welders-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// PARTIES</div>
          <h1 className="font-heading text-3xl font-black text-white">Welders &amp; Fabricators</h1>
          <p className="text-slate-400 text-sm mt-1">
            Your regular business clients. Every order tagged to a welder builds their profile.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setExportOpen(true)}
            variant="outline"
            className="rounded-sm h-10 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="export-welders-pdf-btn"
          >
            <FilePdf size={16} className="mr-1" weight="bold" /> EXPORT PDF
          </Button>
          <Button onClick={() => setEditing("new")}
            className="btn-industrial rounded-sm h-10" data-testid="add-welder-button">
            <Plus size={16} className="mr-1" weight="bold" /> ADD WELDER
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total Welders" value={rows.length} tone="cyan" />
        <MetricCard label="Combined Revenue" value={`₹${totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} />
        <MetricCard label="Outstanding" value={`₹${totalBalance.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
          tone={totalBalance > 0 ? "orange" : "emerald"} />
        <MetricCard label="Avg Revenue"
          value={`₹${rows.length ? (totalRevenue / rows.length).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : 0}`}
          tone="emerald" />
      </div>

      <div className="surface p-4 rounded-sm flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, shop, or mobile…"
            className="pl-9 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
            data-testid="welders-search-input"/>
        </div>
        {/* View toggle */}
        <div className="inline-flex rounded-sm border border-slate-600 overflow-hidden shrink-0" data-testid="welders-view-toggle">
          <button
            onClick={() => setView("cards")}
            className={
              "px-3 h-10 flex items-center gap-1.5 text-xs font-mono-num tracking-widest transition-colors " +
              (viewMode === "cards"
                ? "bg-orange-500 text-white"
                : "bg-slate-800/60 text-slate-300 hover:bg-slate-700")
            }
            data-testid="welders-view-cards"
          >
            <GridFour size={14} weight="bold" /> CARDS
          </button>
          <button
            onClick={() => setView("table")}
            className={
              "px-3 h-10 flex items-center gap-1.5 text-xs font-mono-num tracking-widest transition-colors " +
              (viewMode === "table"
                ? "bg-orange-500 text-white"
                : "bg-slate-800/60 text-slate-300 hover:bg-slate-700")
            }
            data-testid="welders-view-table"
          >
            <Rows size={14} weight="bold" /> TABLE
          </button>
        </div>
      </div>

      {!busy && rows.length === 0 ? (
        <div className="surface p-12 rounded-sm text-center">
          <Buildings size={40} className="mx-auto text-slate-600 mb-3" />
          <div className="font-heading text-xl text-white font-black">No welders yet</div>
          <div className="text-slate-400 mt-1">Add your regular fabricator clients to track their business.</div>
        </div>
      ) : viewMode === "table" ? (
        <WeldersTable rows={rows} onEdit={setEditing} onDelete={del} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((w, i) => (
            <div key={w.id} className={
              "surface p-4 rounded-sm border-l-4 " +
              (i === 0 && w.total_revenue > 0 ? "border-orange-500" : "border-slate-700")
            } data-testid={`welder-card-${w.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-slate-100 font-semibold truncate">{w.name}</div>
                    {i === 0 && w.total_revenue > 0 && (
                      <Trophy size={14} weight="fill" className="text-orange-400" />
                    )}
                  </div>
                  {w.shop_name && (
                    <div className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
                      <Buildings size={11} /> {w.shop_name}
                    </div>
                  )}
                  <a href={`tel:${w.mobile}`} className="text-cyan-300 font-mono-num text-xs flex items-center gap-1 mt-0.5 hover:text-cyan-200">
                    <Phone size={11} /> {w.mobile}
                  </a>
                  {w.address && (
                    <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
                      <MapPin size={10} /> {w.address}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(w)} className="text-slate-500 hover:text-white p-1">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => del(w)} className="text-slate-500 hover:text-red-400 p-1">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <MiniStat label="Orders" value={w.total_orders || 0} icon={<Package size={11}/>} tone="cyan" />
                <MiniStat label="Revenue" value={`₹${(w.total_revenue || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  icon={<CurrencyInr size={11}/>} />
                <MiniStat label="Balance"
                  value={`₹${(w.total_balance || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  tone={w.total_balance > 0 ? "orange" : "emerald"} />
              </div>
            </div>
          ))}
        </div>
      )}

      <WelderEditor
        open={!!editing}
        welder={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
      <ExportPdfDialog open={exportOpen} onClose={() => setExportOpen(false)} />
    </div>
  );
}

function WeldersTable({ rows, onEdit, onDelete }) {
  return (
    <div className="surface rounded-sm overflow-hidden" data-testid="welders-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-800/40 text-[10px] font-mono-num tracking-widest text-slate-400">
              <th className="text-left px-3 py-3 w-10">#</th>
              <th className="text-left px-3 py-3">WELDER / SHOP</th>
              <th className="text-left px-3 py-3">MOBILE</th>
              <th className="text-left px-3 py-3">ADDRESS</th>
              <th className="text-right px-3 py-3">ORDERS</th>
              <th className="text-right px-3 py-3">REVENUE</th>
              <th className="text-right px-3 py-3">PAID</th>
              <th className="text-right px-3 py-3">BALANCE</th>
              <th className="text-right px-3 py-3 w-20">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w, i) => (
              <tr
                key={w.id}
                className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors"
                data-testid={`welder-row-${w.id}`}
              >
                <td className="px-3 py-3 text-slate-500 font-mono-num">{i + 1}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-100 font-semibold truncate">{w.name}</span>
                    {i === 0 && w.total_revenue > 0 && (
                      <Trophy size={12} weight="fill" className="text-orange-400 shrink-0" />
                    )}
                  </div>
                  {w.shop_name && (
                    <div className="text-[11px] text-slate-500 truncate">{w.shop_name}</div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <a href={`tel:${w.mobile}`} className="text-cyan-300 font-mono-num text-xs hover:text-cyan-200">
                    {w.mobile || "—"}
                  </a>
                </td>
                <td className="px-3 py-3 text-slate-400 text-xs truncate max-w-[220px]">{w.address || "—"}</td>
                <td className="px-3 py-3 text-right font-mono-num text-cyan-300">{w.total_orders || 0}</td>
                <td className="px-3 py-3 text-right font-mono-num text-slate-100">
                  ₹{(w.total_revenue || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </td>
                <td className="px-3 py-3 text-right font-mono-num text-emerald-400">
                  ₹{(w.total_paid || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </td>
                <td className={
                  "px-3 py-3 text-right font-mono-num " +
                  (w.total_balance > 0 ? "text-orange-400" : "text-emerald-400")
                }>
                  ₹{(w.total_balance || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(w)}
                      className="text-slate-500 hover:text-white p-1"
                      data-testid={`edit-welder-${w.id}`}
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(w)}
                      className="text-slate-500 hover:text-red-400 p-1"
                      data-testid={`delete-welder-${w.id}`}
                      title="Delete"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExportPdfDialog({ open, onClose }) {
  const pad = (n) => String(n).padStart(2, "0");
  const _now = new Date();
  const today = istToday();
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [useRange, setUseRange] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFrom(monthStart);
      setTo(today);
      setUseRange(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const download = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (useRange) {
        if (from) params.set("from", from);
        if (to) params.set("to", to);
      }
      const qs = params.toString();
      const resp = await api.get(`/welders/export/pdf${qs ? `?${qs}` : ""}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fname = useRange && (from || to)
        ? `welders-report_${from || "start"}_to_${to || "today"}.pdf`
        : `welders-report.pdf`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm" data-testid="welders-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FilePdf size={20} className="text-emerald-400" weight="fill" /> Export Welders PDF
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-slate-400 leading-relaxed">
            Date range se sirf un orders ke totals aayenge jo is period me create hue.
            Range hata do toh <b className="text-orange-300">lifetime</b> totals aayenge.
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
            <input
              type="checkbox"
              checked={useRange}
              onChange={(e) => setUseRange(e.target.checked)}
              className="w-4 h-4 accent-orange-500"
              data-testid="welders-export-userange"
            />
            Filter by date range
          </label>

          <div className={"grid grid-cols-2 gap-3 " + (useRange ? "" : "opacity-40 pointer-events-none")}>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">FROM</Label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                max={to || today}
                className="mt-1 w-full bg-slate-800/60 border border-slate-600 hover:border-orange-500/60 focus:border-orange-500 focus:outline-none text-white h-10 rounded-sm font-mono-num px-2 text-sm"
                data-testid="welders-export-from"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">TO</Label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                min={from}
                max={today}
                className="mt-1 w-full bg-slate-800/60 border border-slate-600 hover:border-orange-500/60 focus:border-orange-500 focus:outline-none text-white h-10 rounded-sm font-mono-num px-2 text-sm"
                data-testid="welders-export-to"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { l: "TODAY", f: today, t: today },
              { l: "THIS MONTH", f: monthStart, t: today },
              { l: "LAST 30D", f: istDate(Date.now() - 30 * 86400000), t: today },
              { l: "THIS YEAR", f: today.slice(0, 4) + "-01-01", t: today },
            ].map((p) => (
              <button
                key={p.l}
                onClick={() => { setUseRange(true); setFrom(p.f); setTo(p.t); }}
                className="text-[10px] font-mono-num tracking-widest px-2.5 py-1 rounded-sm border border-slate-600 text-slate-300 hover:border-orange-500/60 hover:text-orange-300 transition-colors"
                data-testid={`welders-export-preset-${p.l}`}
              >
                {p.l}
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={download}
              disabled={busy}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest px-5"
              data-testid="welders-export-download"
            >
              <FilePdf size={16} className="mr-1.5" weight="fill" />
              {busy ? "GENERATING…" : "DOWNLOAD PDF"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetricCard({ label, value, tone = "orange" }) {
  const t = {
    orange: "text-orange-400 border-orange-500/40",
    cyan: "text-cyan-300 border-cyan-500/40",
    emerald: "text-emerald-400 border-emerald-500/40",
  }[tone];
  return (
    <div className={`surface p-4 rounded-sm border-t-2 ${t}`}>
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400">{label.toUpperCase()}</div>
      <div className={`mt-1 font-heading text-2xl font-black font-mono-num ${t.split(" ")[0]}`}>{value}</div>
    </div>
  );
}
function MiniStat({ label, value, tone = "slate", icon }) {
  const c = { slate: "text-slate-200", cyan: "text-cyan-300", orange: "text-orange-400", emerald: "text-emerald-400" }[tone];
  return (
    <div className="surface-2 px-2.5 py-2 rounded-sm">
      <div className="text-[9px] font-mono-num text-slate-500 tracking-widest flex items-center gap-1">{icon}{label.toUpperCase()}</div>
      <div className={`font-mono-num text-sm font-semibold ${c}`}>{value}</div>
    </div>
  );
}

function WelderEditor({ open, welder, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", shop_name: "", mobile: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(welder ? { ...welder } : { name: "", shop_name: "", mobile: "", address: "", notes: "" });
  }, [open, welder]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) { toast.error("Name and mobile required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        shop_name: form.shop_name || "",
        mobile: form.mobile.trim(),
        address: form.address || "",
        notes: form.notes || "",
      };
      if (welder?.id) await api.patch(`/welders/${welder.id}`, payload);
      else await api.post("/welders", payload);
      toast.success(welder ? "Welder updated" : `Added ${form.name}`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">{welder ? "Edit Welder" : "Add Welder"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="welder-editor-form">
          <Field label="Welder Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              required data-testid="welder-name-input"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"/>
          </Field>
          <Field label="Shop Name (optional)">
            <Input value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
              placeholder="e.g., Sharma Fabrication Works"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"/>
          </Field>
          <Field label="Mobile">
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              required data-testid="welder-mobile-input"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"/>
          </Field>
          <Field label="Address (optional)">
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"/>
          </Field>
          <Button type="submit" disabled={busy}
            className="w-full btn-industrial rounded-sm h-11" data-testid="save-welder-button">
            {busy ? "SAVING…" : (welder ? "SAVE CHANGES" : "ADD WELDER")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">{label.toUpperCase()}</Label>
      {children}
    </div>
  );
}
